import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * shadcn Table primitives with `unstyled` for legacy dual-class
 * (forecast-table / crm-entity-table / etc.). No TanStack — sorting,
 * filtering, and RBAC stay in existing callers.
 */
const Table = React.forwardRef(({ className, unstyled = false, ...props }, ref) => {
  if (unstyled) {
    return <table ref={ref} className={cn(className)} {...props} />;
  }
  return (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
});
Table.displayName = 'Table';

const TableHeader = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(!unstyled && '[&_tr]:border-b', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(!unstyled && '[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      !unstyled && 'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      !unstyled &&
        'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      className
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      !unstyled &&
        'h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      !unstyled &&
        'p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef(({ className, unstyled = false, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(!unstyled && 'mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
